import { useEffect, useRef } from 'react'

import { COMPARE_ARRAY_PARAMS, compareUrl } from '@/lib/directory/compare-url'

import {
  ActionType,
  INITIAL_FILTERS,
  SortOption,
  parseDirectoryQuery,
  type DirectoryAction,
  type DirectoryState,
} from './use-directory-reducer'

const QUERY_KEYS = [...COMPARE_ARRAY_PARAMS, 'brand', 'featureMode', 'sort', 'term']

/** Keep shareable filters in the URL without making the cached story dynamic. */
export const useDirectoryUrl = (state: DirectoryState, dispatch: React.Dispatch<DirectoryAction>) => {
  const restoring = useRef(true)

  useEffect(() => {
    const restore = () => {
      restoring.current = true
      const payload = parseDirectoryQuery(window.location.search)
      dispatch({
        type: ActionType.HydrateFromQuery,
        filters: { ...INITIAL_FILTERS, ...payload.filters },
        sort: payload.sort ?? SortOption.Featured,
        term: payload.term ?? '',
      })
    }
    // Clean and campaign-only URLs already match the server-rendered state.
    // Avoid rerendering the whole workspace just to restore those defaults.
    const query = new URLSearchParams(window.location.search)
    if (QUERY_KEYS.some((key) => query.has(key))) restore()
    window.addEventListener('popstate', restore)
    return () => window.removeEventListener('popstate', restore)
  }, [dispatch])

  useEffect(() => {
    if (restoring.current) {
      restoring.current = false
      return
    }
    const url = new URL(window.location.href)
    const query = new URL(compareUrl(state.filters), url.origin).searchParams
    for (const key of QUERY_KEYS) {
      url.searchParams.delete(key)
    }
    query.forEach((value, key) => {
      if (key !== 'featureMode' || state.filters.hardware.length + state.filters.software.length > 0) {
        url.searchParams.set(key, value)
      }
    })
    if (state.sort !== SortOption.Featured) {
      url.searchParams.set('sort', state.sort)
    }
    if (state.term) {
      url.searchParams.set('term', state.term)
    }
    if (url.href !== window.location.href) {
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    }
  }, [state.filters, state.sort, state.term])
}
